import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import fetchColors, { getColors } from './fetch-colors';
import './HubView.scss';
import getTypeFromUri from './get-type-from-uri';
import getHubItemUri from './get-hub-item-uri';
import store from './store';
import spfetch from './spfetch';
import truncate from './truncate';
import { Flipped, Flipper, spring } from 'react-flip-toolkit';
import ReactCSSTransitionReplace from 'react-css-transition-replace';
import { connect } from 'unistore/full/react';
import actions from './actions';
import produce from 'immer';
import ScrollBooster from 'scrollbooster';
import {mockUserShelf} from './superfriends/mockUserShelf';

import ThumbUpIcon from '@material-ui/icons/ThumbUp'
import ThumbDownIcon from '@material-ui/icons/ThumbDown'

const onAppear = (el, index) =>
  spring({
    onUpdate: (val) => {
      el.style.opacity = val;
    },
  });

const onExit = (el, index, removeElement) => {
  spring({
    config: { overshootClamping: true },
    onUpdate: (val) => {
      el.style.opacity = 1 - val;
    },
    onComplete: removeElement,
  });

  return () => {
    el.style.opacity = '';
    removeElement();
  };
};

function ItemLink({ item, children, onClick, ...rest }) {
  return (
    <Link
      to={getLinkTo(item)}
      onClick={(event) => {
        if (item.onNavigate) item.onNavigate(item, event);
        if (onClick) onClick.call(this, event);
      }}
      {...rest}
    >
      {children}
    </Link>
  );
}

function NoLink({ item, children, onClick, ...rest }) {
  return (
    <div
      onClick={(event) => {
        if (item.onNavigate) item.onNavigate(item);
        if (onClick) onClick.call(this, event);
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

const HubView = connect(['hubViewResponsesByUrl'])(function HubView({
  hubViewResponsesByUrl,
  url,
  data = hubViewResponsesByUrl?.[url],
  noTitle,
  noMargin,
  className = '',
  onNavigate,
  overrides,
  ...rest
}) {
  useEffect(() => {
    console.log({ url });
    if (!url) return;
    const hubUrl = new URL(url);

    hubUrl.searchParams.set(
      'client-timezone',
      Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || 'unknown'
    );
    hubUrl.searchParams.set('locale', navigator.language);
    hubUrl.searchParams.set('offset', '0');
    hubUrl.searchParams.set('platform', 'android');
    hubUrl.searchParams.set('podcast', 'true');

    spfetch(hubUrl.toString()).then((data) => {
      // insert the mock user shelf!
      if (hubUrl) {
        data.body.unshift(mockUserShelf);
      }
      store.setState(
        produce(store.getState(), (draft) => {
          draft.hubViewResponsesByUrl[url] = data;
        })
      )
      });
  }, [url]);

  return (
    <div
      className={`HubView ${noMargin ? 'HubView--no-margin' : ''} ${className}`}
      {...rest}
    >
      {!noTitle && data?.title && (
        <div className="HubView__title">{data?.title}</div>
      )}
      {data?.body
        ?.map((item, index) => {
          const Component = getComponentForId(item?.component?.id, item);
          return (
            Component && (
              <Component
                {...item}
                key={index}
                onNavigate={onNavigate}
                overrides={overrides}
              />
            )
          );
        })
        ?.filter(Boolean)}
    </div>
  );
});

export default HubView;

function getComponentForId(id, item) {
  switch (id) {
    case 'glue:gradient':
      return GlueGradient;

    case 'glue:sectionHeader':
    case 'glue2:sectionHeader':
      return GlueSectionHeader;

    case 'find:categoryCard':
      return findCategoryCard;

    case 'find:tertiaryButton':
      return FindTertiaryButton;

    case 'glue2:card':
      return Glue2Card;

    case 'glue:entityRow':
    case 'glue2:imageRow':
      return GlueEntityRow;

    case 'glue:carousel':
      return GlueCarousel;

    case 'glue:entityCard':
      return GlueEntityCard;

    case 'glue:navigationRow':
      return GlueNavigationRow;

    default:
      console.log(`Unknown HubView component "${id}"`, item);
      break;
  }
}

function GlueGradient(item) {
  const [color, setColor] = useState(
    getColors(item?.images?.main?.uri)?.DESATURATED
  );

  useEffect(() => {
    fetchColors(item?.images?.main?.uri).then((colors) =>
      setColor(colors?.DESATURATED)
    );
  }, [item?.images?.main?.uri]);

  return (
    <div className="HubView__gradient-wrapper">
      <div
        className="HubView__gradient"
        style={{
          '--color': color,
        }}
      />
      <HubView
        data={{ body: item?.children }}
        onNavigate={item?.onNavigate}
        overrides={item?.overrides}
        noMargin
      />
    </div>
  );
}

function GlueSectionHeader(item) {
  return <div className="HubView__section-header">{item?.text?.title}</div>;
}

function findCategoryCard(item) {
  return (
    <ItemLink
      item={item}
      className="HubView__category-card"
      style={{
        backgroundColor: item.custom?.backgroundColor,
        backgroundImage: item.images?.background?.uri
          ? `url(${item.images?.background?.uri})`
          : 'none',
      }}
    >
      <div className="HubView__category-card-title">{item?.text?.title}</div>
    </ItemLink>
  );
}

function Glue2Card(item) {
  const type = getTypeFromUri(item?.target?.uri || item?.metadata?.uri);

  return (
    <ItemLink item={item} className="HubView__glue-card">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        fill="none"
        viewBox="0 0 164 224"
      >
        <text
          fill="#fff"
          style={{ whiteSpace: 'pre' }}
          fontSize="13"
          fontWeight="bold"
        >
          <tspan x="0" y="184.257">
            {truncate(item?.text.title, 24)}
          </tspan>
        </text>
        <text fill="#B3B3B3" style={{ whiteSpace: 'pre' }} fontSize="13">
          <tspan x="0" y="204.257">
            {truncate(item?.text?.subtitle, 24)}
          </tspan>
        </text>

        <rect x="0" y="0" width="164" height="164" fill="#282828" />

        <g transform="translate(64, 64)">
          {type === 'playlist' && (
            <path
              d="M28 4.23199L11 7.84599V22.707C10.175 21.675 8.921 21 7.5 21C5.019 21 3 23.019 3 25.5C3 27.981 5.019 30 7.5 30C9.981 30 12 27.981 12 25.5V8.65499L27.001 5.46699V19.706C26.176 18.674 24.922 18 23.501 18C21.02 18 19.001 20.019 19.001 22.5C19.001 24.981 21.02 27 23.501 27C25.982 27 28.001 24.982 28.001 22.5L28 4.23199ZM7.5 29C5.57 29 4 27.43 4 25.5C4 23.57 5.57 22 7.5 22C9.43 22 11 23.57 11 25.5C11 27.43 9.43 29 7.5 29ZM23.501 26C21.571 26 20.001 24.43 20.001 22.5C20.001 20.57 21.571 19 23.501 19C25.431 19 27.001 20.57 27.001 22.5C27.001 24.43 25.431 26 23.501 26Z"
              fill="#b3b3b3"
            />
          )}
        </g>

        <image
          width="164"
          height="164"
          xlinkHref={getImageUrl(item.images?.main?.uri)}
        />
      </svg>
    </ItemLink>
  );
}

function GlueEntityRow(item) {
  const imageUrl = getImageUrl(item?.images?.main?.uri);
  const type = getTypeFromUri(item?.target?.uri || item?.metadata?.uri);

  return (
    <ItemLink item={item} className="HubView__entity-row">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        fill="none"
        viewBox="0 0 375 64"
      >
        {item?.overrides?.action?.(item) || (
          <path
            fill="#B3B3B3"
            d="M348.129 24.62l-.65.76 7.752 6.62-7.752 6.62.65.76 8.642-7.38-8.642-7.38z"
          ></path>
        )}

        <text
          fill="#B3B3B3"
          style={{ whiteSpace: 'pre' }}
          fontSize="13"
          letterSpacing="0em"
        >
          <tspan x="76" y="48.258">
            {truncate(item?.text?.subtitle, 40)}
          </tspan>
        </text>
        <text
          fill="#fff"
          style={{ whiteSpace: 'pre' }}
          fontSize="16"
          letterSpacing="0em"
        >
          <tspan x="76" y="29.24">
            {truncate(item?.text?.title, 28)}
          </tspan>
        </text>

        {item?.images?.main?.custom?.style === 'circular' ? (
          <g>
            <circle cx="40" cy="32" r="24" fill="#282828" />

            <g transform="translate(28, 16)">
              <path
                d="M15.962 14.421L15.001 13.869C14.824 13.767 14.772 13.607 14.757 13.52C14.742 13.434 14.737 13.266 14.869 13.111L16.18 11.574C17.032 10.58 17.5 9.311 17.5 8V7.5C17.5 5.985 16.866 4.523 15.76 3.486C14.639 2.435 13.179 1.913 11.635 2.011C8.756 2.198 6.501 4.684 6.501 7.671V8C6.501 9.311 6.97 10.58 7.82 11.574L9.131 13.112C9.263 13.267 9.258 13.435 9.243 13.521C9.229 13.607 9.177 13.768 9 13.869L5.787 15.716C4.068 16.692 3 18.526 3 20.501V22H4V20.501C4 18.885 4.874 17.385 6.283 16.584L9.499 14.736C9.887 14.513 10.153 14.132 10.229 13.692C10.305 13.251 10.183 12.803 9.892 12.463L8.58 10.924C7.884 10.111 7.501 9.072 7.501 8V7.671C7.501 5.21 9.346 3.162 11.7 3.01C12.957 2.927 14.16 3.357 15.077 4.216C15.995 5.076 16.5 6.243 16.5 7.5V8C16.5 9.072 16.117 10.111 15.42 10.925L14.109 12.462C13.818 12.803 13.696 13.251 13.772 13.691C13.848 14.131 14.115 14.512 14.503 14.736L14.982 15.011C15.287 14.782 15.61 14.578 15.962 14.421ZM20 11.184V16.761C19.465 16.277 18.763 15.973 17.987 15.973C16.325 15.973 14.973 17.324 14.973 18.987C14.973 20.649 16.325 22.001 17.987 22.001C19.649 22.001 21 20.648 21 18.986V12.915L22.99 14.064L23.49 13.198L20 11.184ZM17.986 21C16.876 21 15.972 20.097 15.972 18.986C15.972 17.876 16.875 16.972 17.986 16.972C19.096 16.972 19.999 17.875 19.999 18.986C20 20.097 19.096 21 17.986 21Z"
                fill="#B3B3B3"
              />
            </g>

            <circle cx="40" cy="32" r="24" fill={`url(#${imageUrl})`} />
          </g>
        ) : (
          <g>
            <rect
              x="16"
              y="8"
              width="48"
              height="48"
              fill="#282828"
              rx={type === 'show' ? 4 : 0}
            />

            <g transform="translate(28, 18)">
              {type === 'playlist' && (
                <path
                  d="M9 6.15899V17.058C8.364 16.407 7.48 16 6.5 16C4.57 16 3 17.57 3 19.5C3 21.43 4.57 23 6.5 23C8.43 23 10 21.43 10 19.5V6.96899L21 4.62999V14.808C20.364 14.157 19.48 13.75 18.5 13.75C16.57 13.75 15 15.32 15 17.25C15 19.18 16.57 20.75 18.5 20.75C20.43 20.75 22 19.18 22 17.25V3.39499L9 6.15899ZM6.5 22C5.122 22 4 20.878 4 19.5C4 18.122 5.122 17 6.5 17C7.878 17 9 18.122 9 19.5C9 20.878 7.878 22 6.5 22ZM18.5 19.75C17.122 19.75 16 18.628 16 17.25C16 15.871 17.122 14.75 18.5 14.75C19.878 14.75 21 15.871 21 17.25C21 18.628 19.878 19.75 18.5 19.75Z"
                  fill="#B3B3B3"
                />
              )}

              {type === 'track' && (
                <path
                  d="M20.25 6.317L13.002 2.133L13.013 16.042C12.378 15.398 11.497 14.996 10.523 14.996C8.593 14.996 7.023 16.566 7.023 18.496C7.023 20.426 8.593 21.996 10.523 21.996C12.38 21.996 13.889 20.538 14.002 18.709C14.002 18.709 14.016 18.441 14.015 18.412L14.003 3.866L19.75 7.183L20.25 6.317ZM10.523 20.997C9.145 20.997 8.023 19.876 8.023 18.497C8.023 17.119 9.145 15.997 10.523 15.997C11.873 15.997 12.969 17.074 13.014 18.413V18.58C12.97 19.919 11.873 20.997 10.523 20.997Z"
                  fill="#B3B3B3"
                />
              )}

              {type === 'show' && (
                <path
                  d="M3.89503 17.995C0.315028 13.802 0.812029 7.47701 5.00603 3.89601C5.29103 3.65201 5.60803 3.41801 6.00203 3.15601L5.45003 2.32201C5.02003 2.60601 4.67303 2.86501 4.35603 3.13501C2.12203 5.04301 0.764028 7.70701 0.534028 10.636C0.303028 13.566 1.22703 16.409 3.13603 18.643C3.79703 19.417 4.57603 20.102 5.45003 20.679L6.00003 19.845C5.20403 19.319 4.49603 18.697 3.89503 17.995ZM7.60303 6.93801C7.90903 6.67601 8.24303 6.44501 8.59303 6.25001L8.10803 5.37601C7.69903 5.60201 7.31103 5.87201 6.95403 6.17701C5.53203 7.39101 4.66803 9.08701 4.52103 10.951C4.37403 12.815 4.96203 14.624 6.17703 16.046C6.71703 16.678 7.36803 17.207 8.11303 17.619L8.59703 16.744C7.95703 16.39 7.39903 15.937 6.93703 15.396C4.78903 12.881 5.08803 9.08601 7.60303 6.93801ZM11.5 8.50001C9.84603 8.50001 8.50003 9.84601 8.50003 11.5C8.50003 12.983 9.58403 14.21 11 14.449V22H12V14.449C13.416 14.21 14.5 12.983 14.5 11.5C14.5 9.84601 13.154 8.50001 11.5 8.50001ZM11.5 13.5C10.398 13.5 9.50003 12.602 9.50003 11.5C9.50003 10.398 10.398 9.50001 11.5 9.50001C12.602 9.50001 13.5 10.398 13.5 11.5C13.5 12.602 12.602 13.5 11.5 13.5ZM16.823 6.95401C16.283 6.32201 15.632 5.79201 14.887 5.38101L14.403 6.25601C15.043 6.60901 15.601 7.06301 16.063 7.60401C17.104 8.82301 17.608 10.374 17.482 11.971C17.356 13.569 16.616 15.022 15.397 16.063C15.092 16.324 14.758 16.555 14.407 16.751L14.892 17.625C15.302 17.397 15.691 17.128 16.046 16.824C17.468 15.61 18.332 13.914 18.479 12.051C18.626 10.188 18.037 8.37601 16.823 6.95401ZM19.864 4.35601C19.204 3.58301 18.426 2.89801 17.551 2.32001L17 3.15501C17.797 3.68001 18.505 4.30301 19.104 5.00601C20.838 7.03701 21.678 9.62201 21.469 12.285C21.26 14.948 20.025 17.369 17.994 19.104C17.707 19.35 17.39 19.584 16.998 19.843L17.549 20.677C17.97 20.399 18.328 20.133 18.643 19.864C20.877 17.956 22.235 15.292 22.465 12.363C22.697 9.43401 21.772 6.59001 19.864 4.35601Z"
                  fill="#B3B3B3"
                />
              )}
            </g>

            <rect
              x="16"
              y="8"
              width="48"
              height="48"
              fill={`url(#${imageUrl})`}
              rx={type === 'show' ? 4 : 0}
            />
          </g>
        )}

        <defs>
          <pattern
            id={imageUrl}
            width="1"
            height="1"
            patternContentUnits="objectBoundingBox"
          >
            <image
              transform="scale(.00156)"
              width="640"
              height="640"
              xlinkHref={imageUrl}
              preserveAspectRatio="xMidYMin slice"
            ></image>
          </pattern>
        </defs>
      </svg>
    </ItemLink>
  );
}

function GlueCarousel(item) {
  return (
    <div className="HubView__carousel">
      {item?.text?.title && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 375 32"
          className="HubView__carousel-title"
        >
          <text
            fill="#fff"
            style={{ whiteSpace: 'pre' }}
            fontSize="16"
            fontWeight="bold"
            letterSpacing="0em"
          >
            <tspan x="16" y="21.24">
              {item?.text?.title}
            </tspan>
          </text>
        </svg>
      )}

      <div
        className="HubView__carousel-items-wrapper"
        ref={(el) =>
          el &&
          new ScrollBooster({
            scrollMode: 'native',
            viewport: el,
            pointerMode: 'mouse',
            direction: 'horizontal',
          })
        }
      >
        <div className="HubView__carousel-items">
          {item?.children
            ?.map((item, index) => {
              const Component = getComponentForId(item?.component?.id, item);
              return Component && <Component {...item} key={index} />;
            })
            ?.filter(Boolean)}
        </div>
      </div>
    </div>
  );
}

function GlueEntityCard(item) {
  const imageUrl = getImageUrl(item?.images?.main?.uri);
  const type = getTypeFromUri(item?.target?.uri);

  return (
    <ItemLink item={item} className="HubView__glue-entity-card">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        fill="none"
        viewBox="0 0 112 160"
      >
        <g clip-path="url(#clip0)" className="HubView__glue-entity-card-title">
          <text
            fill="#fff"
            style={{ whiteSpace: 'pre' }}
            fontSize="13"
            letterSpacing="0em"
          >
            <tspan x="0" y="132.257">
              {item?.text?.title}
            </tspan>
          </text>
        </g>

        <g>
          <rect x="0" y="0" width="112" height="112" fill="#282828" />

          <g transform="translate(40, 40)">
            {type === 'playlist' && (
              <path
                d="M28 4.23199L11 7.84599V22.707C10.175 21.675 8.921 21 7.5 21C5.019 21 3 23.019 3 25.5C3 27.981 5.019 30 7.5 30C9.981 30 12 27.981 12 25.5V8.65499L27.001 5.46699V19.706C26.176 18.674 24.922 18 23.501 18C21.02 18 19.001 20.019 19.001 22.5C19.001 24.981 21.02 27 23.501 27C25.982 27 28.001 24.982 28.001 22.5L28 4.23199ZM7.5 29C5.57 29 4 27.43 4 25.5C4 23.57 5.57 22 7.5 22C9.43 22 11 23.57 11 25.5C11 27.43 9.43 29 7.5 29ZM23.501 26C21.571 26 20.001 24.43 20.001 22.5C20.001 20.57 21.571 19 23.501 19C25.431 19 27.001 20.57 27.001 22.5C27.001 24.43 25.431 26 23.501 26Z"
                fill="#b3b3b3"
              />
            )}
          </g>

          <rect
            name="image"
            className="HubView__glue-entity-card-image"
            x="0"
            y="0"
            width="112"
            height="112"
            fill={`url(#${imageUrl})`}
          />
        </g>

        <defs>
          <pattern
            id={imageUrl}
            width="1"
            height="1"
            patternContentUnits="objectBoundingBox"
          >
            <image
              id="image0"
              width="640"
              height="640"
              xlinkHref={imageUrl}
              preserveAspectRatio="xMidYMin slice"
              transform="scale(.00156)"
            />
          </pattern>
          <clipPath id="clip0">
            <path
              fill="#fff"
              d="M0 0H112V16H0z"
              transform="translate(0 120)"
            ></path>
          </clipPath>
        </defs>
      </svg>
    </ItemLink>
  );
}

function GlueNavigationRow(item) {
  const navtext = item?.text?.title;
  const result = navtext.includes("songs");
  if (result) {
    return(
      <>
        <text style={{width:"250px"}}>Are you satisfied with this experience?</text>
        <div style={{display:"flex",marginTop:"0px"}}>
        <ThumbUpIcon style={{marginRight:"25px"}} noWrap/>
        <ThumbDownIcon noWrap/>
        </div>
      </>
    )
  }
  return (
    <ItemLink item={item} className="HubView__glue-navigation-row">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 375 48">
        <path
          fill="#B3B3B3"
          d="M348.129 16.62l-.65.76 7.752 6.62-7.752 6.62.65.76 8.642-7.38-8.642-7.38z"
        ></path>
        <text
          fill="#fff"
          style={{ whiteSpace: 'pre' }}
          fontSize="16"
          letterSpacing="0em"
        >
          <tspan x="16" y="29.24">
            {navtext}
          </tspan>
        </text>
      </svg>
    </ItemLink>
  );
}

function FindTertiaryButton(item) {
  return (
    <div className="HubView__find-tertiary-button">
      <ItemLink item={item}>
        <button className="HubView__find-tertiary-button-button">
          {item?.text?.title}
        </button>
      </ItemLink>
    </div>
  );
}

const getLinkTo = (item) => {
  return `${getHubItemUri(item)
      ?.replace(/^spotify/, '')
      ?.replace(/:/g, '/')}${
      item.trackUri ? `?trackUri=${item.trackUri}` : ''
    }` || null;
};

const getImageUrl = (uri) => {
  const imageId = uri?.match(
    /^(?:https:\/\/i.scdn.co\/image\/|spotify:image:)([^/]+)$/
  )?.[1];
  const mosaicIds = uri?.match(
    /^(?:https:\/\/mosaic.scdn.co\/300\/|spotify:mosaic:)([^/]+)$/
  )?.[1];
  return imageId
    ? `https://i.scdn.co/image/${imageId}`
    : mosaicIds
    ? `https://mosaic.scdn.co/300/${mosaicIds.replace(/:/g, '')}`
    : uri;
};
